import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, FileSearch, ListChecks, MessageCircle, Stethoscope, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Extras";

const POINTS = [
  [FileSearch, "Reports in plain words", "Tap Simplify on any uploaded lab report or scan – Nirogyam AI explains every value in simple, everyday language."],
  [ListChecks, "Clear next steps", "Get practical suggestions on what to do next and which doctor to consult, with an urgency guide."],
  [MessageCircle, "Niro, available 24×7", "Niro – Nirogyam's AI Agent – answers your questions about bookings, orders, follow-ups and more."],
  [Stethoscope, "An AI co-pilot for doctors", "Smart medicine suggestions with Indian brands and instant drug-interaction & allergy alerts."],
];
const DEMO = [
  { k: "Haemoglobin", v: "9.8 g/dL", s: "low", m: "Slightly low – you may feel tired. Iron-rich food can help; ask a doctor." },
  { k: "Fasting sugar", v: "132 mg/dL", s: "high", m: "A bit above normal. Worth a check with a physician soon." },
  { k: "Thyroid (TSH)", v: "2.1 uIU/mL", s: "normal", m: "Normal – your thyroid is working well." },
];
const TONE = { low: "bg-amber-100 text-amber-800", high: "bg-red-100 text-red-700", normal: "bg-emerald-100 text-emerald-800" };

function Demo() {
  const [n, setN] = useState(0);
  useEffect(() => { const t = setInterval(() => setN((x) => (x + 1) % DEMO.length), 2800); return () => clearInterval(t); }, []);
  const d = DEMO[n];
  return (
    <div className="relative mx-auto w-full max-w-sm" data-testid="ai-showcase-demo">
      <div className="relative overflow-hidden rounded-2xl border border-white/15 bg-white/5 p-5 backdrop-blur">
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">Lab report · CBC</p>
        {[80, 64, 72, 56, 68].map((w, i) => <div key={i} className="mt-3 h-2 rounded-full bg-white/15" style={{ width: `${w}%` }} />)}
        <div className="ai-scan absolute inset-x-0 h-16 bg-gradient-to-b from-transparent via-[#FF9700]/25 to-transparent" />
      </div>
      <div key={n} className="feat-swap -mt-6 ml-8 rounded-2xl bg-[#FFFFFF] p-4 shadow-2xl" style={{ color: "#17232E" }}>
        <p className="flex items-center gap-1.5 text-[11px] font-semibold text-[#2412B5]"><Sparkles className="h-3.5 w-3.5" />Nirogyam AI</p>
        <div className="mt-2 flex items-center justify-between"><p className="text-sm font-semibold">{d.k} <span className="font-normal text-[#657581]">{d.v}</span></p>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${TONE[d.s]}`}>{d.s}</span></div>
        <p className="mt-1 text-sm text-[#657581]">{d.m}</p>
      </div>
    </div>
  );
}

export function AiShowcase() {
  const nav = useNavigate();
  return (
    <section id="nirogyam-ai" className="relative overflow-hidden bg-[#140A6B] text-white" data-testid="ai-showcase-section">
      <div className="pointer-events-none absolute -top-32 -right-24 h-96 w-96 rounded-full bg-[#2412B5] blur-3xl opacity-70 blob" />
      <div className="pointer-events-none absolute -bottom-40 -left-20 h-96 w-96 rounded-full bg-[#086477] blur-3xl opacity-50 blob" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal><span className="inline-flex items-center gap-2 rounded-full border border-[#FF9700]/50 bg-[#FF9700]/10 px-3 py-1 text-xs font-semibold text-[#FFB547]" data-testid="ai-showcase-badge"><Sparkles className="h-3.5 w-3.5" />Landmark innovation · New</span>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">Meet <span className="text-[#FFB547]">Nirogyam AI</span>.<br />Your health reports, finally in plain words.</h2>
            <p className="mt-5 max-w-xl text-white/70">A teleclinic with its own built-in AI that reads your reports and explains them simply – so you understand your health before you even meet your doctor.</p></Reveal>
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {POINTS.map(([I, t, d], i) => (
              <Reveal key={t} delay={i * 90}><div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-colors hover:bg-white/[0.08]" data-testid={`ai-point-${i}`}>
                <I className="h-5 w-5 text-[#FFB547]" /><p className="mt-3 font-semibold">{t}</p><p className="mt-1 text-sm text-white/65">{d}</p></div></Reveal>))}
          </div>
          <Reveal delay={300}><div className="mt-8 flex flex-wrap items-center gap-4">
            <Button size="lg" className="rounded-full bg-[#FFFFFF] text-[#140A6B] hover:bg-[#FFFFFF]/90" onClick={() => nav("/patient/records")} data-testid="ai-showcase-cta">Try Nirogyam AI free <ArrowRight className="h-4 w-4 ml-2" /></Button>
            <p className="text-xs text-white/50 max-w-xs">For understanding only – not a diagnosis. Always discuss with a doctor.</p></div></Reveal>
        </div>
        <div className="lg:col-span-5"><Demo /></div>
      </div>
    </section>
  );
}
